import {
  Grid,
  useTheme,
  Stack,
  GridBreak,
  RichTextReader,
  Typography,
  Box,
  Button,
  Card,
  deepCopy,
} from "@compass/core";
import { useMediaQuery, InputLabel, FormControl, NativeSelect } from "@mui/material";
import { FilePanel, Input, Select } from "components";
import { useEffect, useMemo, useState } from "react";
import { FormQuestion, FormResponse, PublicSurveyPageDto, PublicSurveySectionDto, Form, UploadFile } from "generated/openApi";
import { MatrixGroup, MatrixOption, MatrixQuestion } from "../common/MatrixGroup";
import { RadioGroup, RadioOption } from "../common/RadioGroup";
import { CheckboxGroup } from "../common/CheckboxGroup";
import { TransientFormAnswer, TransientQuestionResponse, responseHasAnswer } from "../common/TransientQuestionResponse";
import { FormQuestionTypes } from "pages/cobro/surveys/SurveyPage/Questions/SurveyQuestionEnum";
import { SignatureBox } from "../common/SignatureBox";
import { OpenEndedQuestion } from "../common/OpenEndedQuestion";
import { DropdownQuestion } from "../common/DropdownQuestion";
import { DoubleMatrixGroup } from "../common/DoubleMatrixGroup";
import { initDataModel } from "utils";

type publicSurveyQuestionPageProps = {
  transientQuestionResponses: TransientQuestionResponse[];
  currentPageIndex: number;
  maxPageIndex: number;
  validationErrors: object | undefined;
  page: PublicSurveyPageDto;
  isViewMode: boolean;
  surveyResponse?: FormResponse;
  survey: Form;

  increasePageIndex: (currentIndex: number) => void;
  decreasePageIndex: (currentIndex: number) => void;
  updateQuestionResponse: (value: TransientQuestionResponse) => void;
  submitResponse: () => void;
  isSubmitting?: boolean;

  isPrinting: boolean;
  printCallBack: () => void;
};

export const PublicSurveyQuestionPage = ({
  transientQuestionResponses,
  currentPageIndex,
  maxPageIndex,
  validationErrors,
  page,
  isViewMode,
  surveyResponse,
  survey,
  isPrinting,
  increasePageIndex,
  decreasePageIndex,
  updateQuestionResponse,
  submitResponse,
  isSubmitting = false,
  printCallBack
}: publicSurveyQuestionPageProps) => {
  const theme = useTheme();
  const [endQuestion, setEndQuestion] = useState<FormQuestion>();

  useEffect(() => {
    if (isPrinting) {
      // A micro-delay ensures the DOM has updated and painted the changes
      const timer = setTimeout(() => {
        printCallBack();
      }, 50); 
  
      return () => clearTimeout(timer);
    }
  }, [isPrinting, printCallBack]);

  const renderRadioAnswers = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    const options: RadioOption[] = [];
    question.formAnswers!.map((f) =>
      options.push({
        label: f.name,
        valueType: "AnswerId",
        value: f.id,
        isOther: f.isOther,
        isOtherValue: transientResponse.formAnswers.find((x) => x.formAnswerId == f.id)?.writeIn || null,
        otherTypeId: f.otherWriteInTypeId,
      })
    );

    return (
      <RadioGroup
        isDisabled={isViewMode}
        value={transientResponse.formAnswers[0]?.formAnswerId || null}
        options={options}
        onChange={(option) => {
          if (option.valueType == "EntityId") {
            var temp: TransientFormAnswer = { formEntityId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          } else {
            var temp: TransientFormAnswer = { formAnswerId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          }
        }}
        onOtherChange={(formAnswerId, otherValue) => {
          var changedOption = options.find((o) => o.value == formAnswerId);
          changedOption!.isOtherValue = otherValue;
          var temp: TransientFormAnswer = { formAnswerId: formAnswerId, writeIn: otherValue };
          updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
        }}
      />
    );
  };

  const renderMultipleChoice = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    const options: RadioOption[] = [];
    question.formAnswers!.map((f) =>
      options.push({
        label: f.name,
        valueType: "AnswerId",
        value: f.id,
        isOther: f.isOther,
        isOtherValue: transientResponse.formAnswers.find((x) => x.formAnswerId == f.id)?.writeIn || null,
      })
    );

    return (
      <CheckboxGroup
        isDisabled={isViewMode}
        values={transientResponse.formAnswers.flatMap((x) => x.formAnswerId!)}
        options={options}
        onChange={(optionValue, isChecked) => {
          var temp = deepCopy(transientResponse.formAnswers);
          if (isChecked) {
            temp.push({ formAnswerId: optionValue });
          } else {
            var removeIndex = temp.findIndex((t) => t.formAnswerId == optionValue);
            temp.splice(removeIndex, 1);
          }
          updateQuestionResponse({ ...transientResponse, formAnswers: temp });
        }}
        onOtherChange={(formAnswerId, otherValue) => {
          var temp = deepCopy(transientResponse.formAnswers);
          var changedOption = temp.find((o) => o.formAnswerId == formAnswerId);
          changedOption!.writeIn = otherValue;

          updateQuestionResponse({ ...transientResponse, formAnswers: temp });
        }}
      />
    );
  };

  const renderDropdown = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    const options: RadioOption[] = [];
    question.formAnswers!.map((f) =>
      options.push({
        label: f.name,
        valueType: "AnswerId",
        value: f.id,
        isOther: f.isOther,
        isOtherValue: transientResponse.formAnswers.find((x) => x.formAnswerId == f.id)?.writeIn || null,
        otherTypeId: f.otherWriteInTypeId,
      })
    );

    return (
      <DropdownQuestion
        widthPercent={question.writeInWidthPercent || 100}
        value={transientResponse.formAnswers[0]?.formAnswerId || null}
        options={options}
        isDisabled={isViewMode}
        onChange={(option) => {
          if (option.valueType == "EntityId") {
            var temp: TransientFormAnswer = { formEntityId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          } else {
            var temp: TransientFormAnswer = { formAnswerId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          }
        }}
        onOtherChange={(formAnswerId, otherValue) => {
          var changedOption = options.find((o) => o.value == formAnswerId);
          changedOption!.isOtherValue = otherValue;
          var temp: TransientFormAnswer = { formAnswerId: formAnswerId, writeIn: otherValue };
          updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
        }}
      />
    );
  };

  const renderSignature = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    let signature = transientResponse.signatureFile;
    let matchedResponse = surveyResponse?.formQuestionResponses?.find((r) => r.formQuestionId == question.id);

    return (
      <>
        {!isViewMode && (
          <SignatureBox
            key={question.id}
            width={350}
            height={200}
            languageId={survey.languageId}
            signature={signature}
            onUpdate={(sig) => {
              updateQuestionResponse({ ...transientResponse, signatureFile: sig });
            }}
          />
        )}
        {isViewMode && matchedResponse && (
          <img
            src={matchedResponse.signatureUploadFile?.imagePreviewData}
            style={{ maxWidth: "100%", maxHeight: 250 }}
          />
        )}
      </>
    );
  };

  const renderFileUpload = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    let fileUpload = transientResponse.fileUpload;
    let matchedResponse = surveyResponse?.formQuestionResponses?.find((r) => r.formQuestionId == question.id);

    return (
      <>
        {isViewMode && (
          <FilePanel
            fileName={transientResponse.fileUpload?.fileName}
            fileSize={transientResponse.fileUpload?.fileSize}
            fileRef={transientResponse.fileUpload?.fileRef}
            imagePreviewData={transientResponse.fileUpload?.imagePreviewData}
          />
        )}

        {!isViewMode && (
          <FilePanel
            fileName={transientResponse.fileUpload?.fileName}
            fileSize={transientResponse.fileUpload?.fileSize}
            fileRef={transientResponse.fileUpload?.fileRef}
            imagePreviewData={transientResponse.fileUpload?.imagePreviewData}
            uploadingData={transientResponse.fileUpload?.uploadingData}
            onFileChange={(fileName, fileSize, uploadingData) => {
              const newFile = initDataModel<UploadFile>({
                fileName: fileName,
                fileSize: fileSize,
                uploadingData: uploadingData,
                fileRef: undefined,
                imagePreviewData: undefined
              });

              updateQuestionResponse({ ...transientResponse, fileUpload: newFile });
            }}
          />
        )}
      </>
    );
  };

  const renderParentSignature = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    let matchedResponse = surveyResponse?.formQuestionResponses?.find((r) => r.formQuestionId == question.id);
    return (
      <Grid container spacing="sm">
        {!isViewMode && matchedResponse?.signatureUploadFileId == undefined && (
          <Grid item xs={12}>
            <SignatureBox
              key={question.id}
              width={350}
              height={200}
              languageId={survey.languageId}
              signature={transientResponse.signatureFile}
              onUpdate={(sig) => {
                updateQuestionResponse({ ...transientResponse, signatureFile: sig });
              }}
            />
          </Grid>
        )}
        {isViewMode && matchedResponse && (
          <img
            src={matchedResponse.signatureUploadFile?.imagePreviewData}
            style={{ maxWidth: "100%", maxHeight: 250 }}
          />
        )}
        <Grid item xs={12}>
          <Input
            label="Parent Name"
            sx={{ maxWidth: `100%` }}
            value={transientResponse.writeIn}
            required={true}
            placeholder={survey.languageId == 2 ? "Nombre del Padre" : "Parent Name..."}
            disabled={isViewMode}
            onChange={(e) => {
              updateQuestionResponse({
                ...transientResponse,
                writeIn: e.target.value,
              });
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderMatrix = (question: FormQuestion, transientResponse: TransientQuestionResponse) => {
    const subQuestions: MatrixQuestion[] = [];
    const options: MatrixOption[] = [];
    question.formSubQuestions!.map((sq) =>
      subQuestions.push({ label: sq.label, question: sq.name, subQuestionId: sq.id })
    );
    question.formAnswers!.map((fa) => options.push({ label: fa.name, formAnswerId: fa.id }));

    return (
      <MatrixGroup
        formQuestion={question!}
        isDisabled={isViewMode}
        values={transientResponse.formAnswers}
        subQuestions={subQuestions}
        options={options}
        handleChange={(optionValue, isChecked) => {
          var temp = deepCopy(transientResponse.formAnswers);
          if (question.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice) {
            if (isChecked) {
              temp.push(optionValue);
            } else {
              var removeIndex = temp.findIndex((t) => t.formAnswerId == optionValue.formAnswerId);
              temp.splice(removeIndex, 1);
            }
          } else if (question.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn) {
            const tempIndex: number = temp.findIndex(
              (x) => x.subQuestionId == optionValue.subQuestionId && x.formAnswerId == optionValue.formAnswerId
            );
            if (tempIndex != -1) {
              temp[tempIndex] = optionValue;
            } else {
              temp.push(optionValue);
            }
          } else {
            const tempIndex: number = temp.findIndex((x) => x.subQuestionId == optionValue.subQuestionId);
            if (tempIndex != -1) {
              temp[tempIndex] = optionValue;
            } else {
              temp.push(optionValue);
            }
          }

          updateQuestionResponse({ ...transientResponse, formAnswers: temp });
        }}
      />
    );
  };

  const renderDoubleMatrix = (question: FormQuestion, transientResponse: TransientQuestionResponse, index: Number) => {
    const subQuestions: MatrixQuestion[] = [];
    const options: MatrixOption[] = [];
    question.formSubQuestions!.map((sq) =>
      subQuestions.push({ label: sq.label, question: sq.name, subQuestionId: sq.id, branchedFromSubQuestionId: sq.branchedFromSubQuestionId })
    );
    question.formAnswers!.map((fa) => options.push({ label: fa.name, formAnswerId: fa.id }));

    return (
      <Grid item xs={12} id={`questionId${question.id}`}>
        <Card sx={{ maxWidth: "1100px", minWidth: "440px", width: "100%" }}>
          <Grid container>
            <Grid item xs={12}>
              <DoubleMatrixGroup
                formQuestion={question!}
                isDisabled={isViewMode}
                values={transientResponse.formAnswers}
                subQuestions={subQuestions}
                options={options}
                handleChange={(optionValue, isChecked) => {
                  var temp = deepCopy(transientResponse.formAnswers);
                  if (question.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice) {
                    if (isChecked) {
                      temp.push(optionValue);
                    } else {
                      var removeIndex = temp.findIndex((t) => t.formAnswerId == optionValue.formAnswerId);
                      temp.splice(removeIndex, 1);
                    }
                  } else if (question.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn) {
                    const tempIndex: number = temp.findIndex(
                      (x) => x.subQuestionId == optionValue.subQuestionId && x.formAnswerId == optionValue.formAnswerId
                    );
                    if (tempIndex != -1) {
                      temp[tempIndex] = optionValue;
                    } else {
                      temp.push(optionValue);
                    }
                  } else {
                    const tempIndex: number = temp.findIndex((x) => x.subQuestionId == optionValue.subQuestionId);
                    if (tempIndex != -1) {
                      temp[tempIndex] = optionValue;
                    } else {
                      temp.push(optionValue);
                    }
                  }

                  updateQuestionResponse({ ...transientResponse, formAnswers: temp });
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    );
  };

  const hasUnansweredQuestions = (): boolean => {
    let hasUnanswered: boolean = false;

    page.sections?.forEach((s) => {
      if (!hasUnanswered) {
        var mandatoryQuestions = s.questions?.filter((q) => q.isMandatory);
        if (mandatoryQuestions && mandatoryQuestions?.length > 0) {
          mandatoryQuestions?.forEach((m) => {
            var response = transientQuestionResponses.find((t) => t.questionId == m.id);
            if (response == null || !responseHasAnswer(response)) {
              hasUnanswered = true;
            }
          });
        }
      }
    });

    return hasUnanswered;
  };

  const hasEndQuestion = (): boolean => {
    if (endQuestion) return true;

    let hasEndQ: boolean = false;

    page.sections?.forEach((s) => {
      if (!hasEndQ) {
        var endQuestion = s.questions?.find((q) => q.isEndQuestion);
        if (endQuestion) {
          hasEndQ = true;
          setEndQuestion(endQuestion);
        }
      }
    });

    return hasEndQ;
  };

  const canIncreasePageIndex = (): boolean => {
    if (currentPageIndex != maxPageIndex) return true;
    else return false;
  };

  const renderQuestion = (question: FormQuestion, index: number) => {
    let questionResponseIndex: number = transientQuestionResponses.findIndex((qr) => qr.questionId == question.id)!;
    let transientResponse: TransientQuestionResponse = transientQuestionResponses![questionResponseIndex];

    let questionName = question.name;
    if (question.isMandatory) {
      var index = question.name.indexOf("</div>");
      if (index !== -1) {
        var modifiedString =
          question.name.substring(0, index) + '<span style="color:red">*</span>' + question.name.substring(index);
        questionName = modifiedString;
      }
    }

    return (
      <>
        {((question.formQuestionTypeId != FormQuestionTypes.DoubleMatrix && !endQuestion) ||
          (endQuestion && (question.id == endQuestion.id || question.displayOrder < endQuestion.displayOrder))) && (
            <Grid item xs={12} key={index} id={`questionId${question.id}`}>
              <Card sx={{ maxWidth: "1100px", minWidth: "440px", width: "100%" }}>
                <Grid container direction="row" spacing="lg">
                  <Grid item xs={12}>
                    <Typography color="error">{validationErrors?.[`questionId${question.id}`]}</Typography>
                  </Grid>

                  {!question.hideLabel && question.formQuestionTypeId != FormQuestionTypes.MatrixGrouped && (
                    <Grid item xs={1}>
                      <Typography sx={{ marginTop: "1em" }}>{question?.label}</Typography>
                    </Grid>
                  )}

                  <Grid item xs={question.hideLabel ? 12 : 11}>
                    <Grid container>
                      {question.formQuestionTypeId != FormQuestionTypes.MatrixGrouped && (
                        <Grid item xs={12}>
                          <Stack direction="column">
                            <Stack direction="row" alignItems="center">
                              <RichTextReader
                                html={questionName}
                                customStyles={{
                                  div: {
                                    marginBottom: "1em",
                                    marginTop: "1em",
                                  },
                                }}
                              />
                            </Stack>
                            {question.instruction && question.formQuestionTypeId != FormQuestionTypes.MatrixGrouped && (
                              <RichTextReader html={question.instruction} />
                            )}
                          </Stack>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        {question.formQuestionTypeId == FormQuestionTypes.OpenEnded && (
                          <OpenEndedQuestion
                            isDisabled={isViewMode}
                            question={question}
                            transientResponse={transientResponse}
                            updateQuestionResponse={updateQuestionResponse}
                          />
                        )}
                        {question.formQuestionTypeId == FormQuestionTypes.MultipleChoiceDropDown && (
                          <>{renderDropdown(question, transientResponse)}</>
                        )}
                        {question.formQuestionTypeId == FormQuestionTypes.MultipleChoiceRadio && (
                          <>{renderRadioAnswers(question, transientResponse)}</>
                        )}
                        {question.formQuestionTypeId == FormQuestionTypes.MultipleChoiceCheckBox && (
                          <>{renderMultipleChoice(question, transientResponse)}</>
                        )}
                        {(question.formQuestionTypeId == FormQuestionTypes.Matrix ||
                          question.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice ||
                          question.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn ||
                          question.formQuestionTypeId == FormQuestionTypes.MatrixGrouped) && (
                            <>{renderMatrix(question, transientResponse)}</>
                          )}
                        {question.formQuestionTypeId == FormQuestionTypes.Signature && (
                          <>{renderSignature(question, transientResponse)}</>
                        )}
                        {question.formQuestionTypeId == FormQuestionTypes.SignatureParent && (
                          <>{renderParentSignature(question, transientResponse)}</>
                        )}
                        {question.formQuestionTypeId == FormQuestionTypes.FileUpload && (
                          <>{renderFileUpload(question, transientResponse)}</>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          )}
        {question.formQuestionTypeId == FormQuestionTypes.DoubleMatrix && (
          <>{renderDoubleMatrix(question, transientResponse, index)}</>
        )}
      </>
    );
  };

  function isNullOrWhitespace(input: string) {
    return input.replace(/<\/?[^>]+(>|$)/g, "").trim().length === 0;
  }

  const renderSection = (chunk: PublicSurveySectionDto, index: number) => {
    return (
      <Grid item xs={12} key={index} sx={{ padding: isPrinting ? "30px" : "inherited" }}>
        <Grid container spacing="md">
          {chunk.section && !isNullOrWhitespace(chunk.section?.name) && (
            <Grid item xs={12} pb={2}>
              <Card
                header={" "}
                headerSx={{
                  color: "white",
                  backgroundColor: (theme) => survey.themeColor || theme.palette.primary.dark,
                  fontSize: "1.3rem",
                  fontWeight: "bold",
                }}
                sx={{ maxWidth: "1100px", minWidth: "440px", width: "100%" }}
              >
                <Stack direction="column">
                  <RichTextReader html={chunk.section?.name} />
                  {chunk.section?.instruction && <RichTextReader html={chunk.section?.instruction} />}
                </Stack>
              </Card>
            </Grid>
          )}

          {chunk.questions?.length! > 0 && chunk.questions?.map((q, index) => renderQuestion(q, index))}
        </Grid>
      </Grid>
    );
  };

  return (
    <Stack px={3} py={3} spacing="lg" width="100%" paddingLeft={0} paddingRight={0}>  
      <Grid container spacing="sm">
        {page && page.sections?.map((s, index) => renderSection(s, index))}

        <GridBreak />

        {!isPrinting && (<Grid item xs={12}>
          <Stack direction="row" justifyContent={"space-between"} alignItems="center" pt={2}>
            {currentPageIndex != 1 && (
              <Button variant="contained" onClick={() => decreasePageIndex(currentPageIndex)}>
                {survey.languageId == 2 ? "Anterior" : "Previous"}
              </Button>
            )}
            {currentPageIndex == 1 && <Box />}

            {canIncreasePageIndex() && !hasEndQuestion() && (
              <Button
                // disabled={hasUnansweredQuestions()}
                variant="contained"
                onClick={() => increasePageIndex(currentPageIndex)}
              >
                {survey.languageId == 2 ? "Proximo" : "Next"}
              </Button>
            )}

            {!isViewMode && (!canIncreasePageIndex() || hasEndQuestion()) && (
              <Button variant="contained" disabled={isSubmitting} onClick={() => submitResponse()}>
                {isSubmitting
                  ? survey.languageId == 2 ? "Enviando..." : "Submitting..."
                  : survey.languageId == 2 ? "Enviar" : "Submit"}
              </Button>
            )}
          </Stack>
        </Grid>)}
      </Grid>
    </Stack>
  );
};
